import { MILESTONES, PATH_KEYS } from './flowFunnel.js';

// The one place that knows the wire shape of OBF-1's onboarding flow routes.
// If those responses change shape, this file is the fix.

const list = (value) => (Array.isArray(value) ? value : []);
const unwrap = (payload) => payload?.data ?? payload ?? {};

function count(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function duration(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

const text = (value) => (value === null || value === undefined || value === '' ? null : String(value));

function readStat(raw) {
  return {
    step: String(raw.step),
    reached: count(raw.reached),
    continued: count(raw.continued),
    back: count(raw.back),
    exitedHere: count(raw.exitedHere) + count(raw.abandonedHere),
    abandonedHere: count(raw.abandonedHere),
    stuckNow: count(raw.stuckNow),
    medianMs: duration(raw.timeOnStepMs?.median),
    p90Ms: duration(raw.timeOnStepMs?.p90),
    errors: list(raw.topErrors).map((error) => ({ code: String(error.code), count: count(error.count) })),
    choices: list(raw.topChoices).map((choice) => ({ value: String(choice.choice), count: count(choice.flows ?? choice.count) })),
  };
}

export function readFlowReport(payload) {
  const root = unwrap(payload);
  const totals = root.totals ?? {};
  const paths = { all: root.steps, ...root.byAudience };
  return {
    totals: {
      started: count(totals.started),
      completed: count(totals.completed),
      exitedToSignIn: count(totals.exitedToSignIn),
      abandoned: count(totals.abandoned),
      stuck: count(totals.stuck),
    },
    paths: Object.fromEntries(
      PATH_KEYS.map((key) => [key, list(paths[key]).filter((stat) => stat?.step).map(readStat)]),
    ),
    cohorts: list(root.cohorts)
      .filter((cohort) => cohort?.day)
      .map((cohort) => ({
        day: String(cohort.day).slice(0, 10),
        started: count(cohort.started),
        reached: Object.fromEntries(MILESTONES.map(({ key }) => [key, count(cohort.reached?.[key])])),
      })),
    appVersions: list(root.filterOptions?.appVersions).map(String),
  };
}

function readUser(raw) {
  if (!raw || !(raw.id || raw.name || raw.email)) return null;
  return {
    id: text(raw.id),
    name: text(raw.name),
    email: text(raw.email),
    organizationName: text(raw.organization?.name),
  };
}

export function readStepFlows(payload) {
  const root = unwrap(payload);
  const flows = list(root.flows).map((flow) => ({
    flowId: String(flow.flowId),
    installId: text(flow.installId),
    user: readUser(flow.user),
    lastEvent: { event: text(flow.lastEvent?.event), step: text(flow.lastEvent?.step), at: text(flow.lastEvent?.at) },
  }));
  return { flows, total: Math.max(count(root.total), flows.length) };
}

function readDetails(raw) {
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw).map(([key, value]) => [key, String(value)]);
}

function readEvent(raw) {
  return {
    source: raw.source === 'server' ? 'server' : 'client',
    at: text(raw.at),
    event: text(raw.event),
    step: text(raw.step),
    category: text(raw.category),
    details: readDetails(raw.details),
    from: text(raw.from),
    to: text(raw.to),
    via: text(raw.via),
    ms: duration(raw.ms),
    choice: text(raw.choice),
    code: text(raw.code),
  };
}

export function readFlowTimeline(payload) {
  const root = unwrap(payload);
  const flow = root.flow ?? {};
  return {
    flow: {
      flowId: text(flow.flowId),
      installId: text(flow.installId),
      user: readUser(flow.user),
      audience: text(flow.audience),
      platform: text(flow.platform),
      appVersion: text(flow.appVersion),
      build: text(flow.build),
      variant: text(flow.variant),
      startedAt: text(flow.startedAt),
    },
    events: list(root.timeline)
      .map(readEvent)
      .sort((a, b) => (Date.parse(a.at) || 0) - (Date.parse(b.at) || 0)),
  };
}

const LIST_STATES = { stuck: 'stuck', exited: 'exited,abandoned' };

export function stepFlowsQuery(filters, kind) {
  return { ...flowQuery(filters), state: LIST_STATES[kind] };
}

export function flowQuery(filters) {
  return {
    from: filters.from,
    to: filters.to,
    variant: filters.variant,
    ...(filters.audience !== 'all' ? { audience: filters.audience } : {}),
    ...(filters.appVersion ? { appVersion: filters.appVersion } : {}),
    ...(filters.platform !== 'all' ? { platform: filters.platform } : {}),
  };
}
